/*
 * FONCIER CLAIR — outil privé AUPRÉA (prescription acquisitive outre-mer).
 * Charte dédiée : bleu marine #1B3A6B · or #C9A227 (uniquement CTA monétaires)
 * · teal #0F6E56 · rouge #9C3B3B. Polices Playfair Display + DM Sans + IBM Plex Mono.
 * Page NON indexable (meta robots noindex,nofollow), hors sitemap et hors navigation.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../api";

const FC = {
  bg: "#F3F5F9",
  surface: "#FFFFFF",
  surfaceAlt: "#EAEFF6",
  ink: "#0D2040",
  muted: "#45536B",
  line: "#D7DEE8",
  navy: "#1B3A6B",
  navyDark: "#0D2040",
  teal: "#0F6E56",
  gold: "#C9A227",
  red: "#9C3B3B",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_SANS = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', 'Courier New', monospace";

const TERRITOIRES = [
  { v: "Guadeloupe", l: "Guadeloupe" },
  { v: "Martinique", l: "Martinique" },
  { v: "Guyane", l: "Guyane" },
  { v: "La Réunion", l: "La Réunion" },
  { v: "Mayotte", l: "Mayotte" },
  { v: "Saint-Martin", l: "Saint-Martin" },
  { v: "Saint-Barthélemy", l: "Saint-Barthélemy" },
];
const TYPES_BIEN = [
  { v: "terrain", l: "Un terrain nu" },
  { v: "bati", l: "Une maison ou un bâti" },
  { v: "autre", l: "Autre ou je ne sais pas" },
];
const YN_NSP = [
  { v: "oui", l: "Oui" },
  { v: "non", l: "Non" },
  { v: "je ne sais pas", l: "Je ne sais pas" },
];
const YN = [
  { v: "oui", l: "Oui" },
  { v: "non", l: "Non" },
];
const DOCS_LIST = [
  { k: "factures", l: "Factures/quittances au nom du possesseur à l'adresse du bien" },
  { k: "taxe_fonciere", l: "Avis de taxe foncière payés au nom du possesseur" },
  { k: "photos", l: "Photographies datées" },
  { k: "temoignages", l: "Témoignages écrits" },
  { k: "constat", l: "Constat de commissaire de justice" },
  { k: "justificatif", l: "Justificatif de domicile officiel" },
  { k: "acte_titre", l: "Acte ou titre même imparfait" },
];
const CURRENT_YEAR = new Date().getFullYear();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = [
  { id: 0, label: "Le bien" },
  { id: 1, label: "Possession" },
  { id: 2, label: "Titre & preuves" },
  { id: 3, label: "Résultat" },
];

function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow,noarchive,nosnippet";
    document.head.appendChild(meta);
    const linkFont = document.createElement("link");
    linkFont.rel = "stylesheet";
    linkFont.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap";
    document.head.appendChild(linkFont);
    const oldTitle = document.title;
    document.title = "Foncier Clair · Diagnostic privé";
    return () => {
      document.head.removeChild(meta);
      try { document.head.removeChild(linkFont); } catch (_e) {}
      document.title = oldTitle;
    };
  }, []);
}

export default function FoncierClair() {
  useNoIndex();
  const [params, setParams] = useSearchParams();
  const initialToken = params.get("dossier") || "";
  const [token, setToken] = useState(initialToken);

  // Si un token est déjà présent dans l'URL, on saute directement à la page résultat
  const initialStep = initialToken ? 4 : 0;
  const [step, setStep] = useState(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [situation, setSituation] = useState({ territoire: "", type_bien: "", succession_indivision: "", annee_debut: "" });
  const [possession, setPossession] = useState({ continue: "", paisible: "", publique: "", comportement_proprio: "", conteste: "" });
  const [titre, setTitre] = useState({ juste_titre: "", bonne_foi: "" });
  const [documents, setDocuments] = useState([]); // array of keys ; "aucun" exclusif

  // Coordonnées / consentements (checkout)
  const [contact, setContact] = useState({ prenom: "", nom: "", email: "", telephone: "" });
  const [wantsCallback, setWantsCallback] = useState("");
  const [consentTransmission, setConsentTransmission] = useState(false);
  const [waiveRetractation, setWaiveRetractation] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [checkoutMode, setCheckoutMode] = useState(false); // affiche l'écran paiement
  const [pollingPayment, setPollingPayment] = useState(false);
  const [dossier, setDossier] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step, checkoutMode]);

  const territoireOM = useMemo(() => {
    const t = (situation.territoire || "").toLowerCase();
    return ["guadeloupe", "martinique", "guyane", "la réunion", "mayotte", "saint-martin", "saint-barthélemy"].includes(t);
  }, [situation.territoire]);

  const annees = useMemo(() => {
    const y = parseInt(situation.annee_debut, 10);
    if (!y || y < 1900 || y > CURRENT_YEAR) return null;
    return CURRENT_YEAR - y;
  }, [situation.annee_debut]);

  // ---- Load existing dossier from token
  const loadDossier = useCallback(async (tk) => {
    if (!tk) return;
    setError("");
    try {
      const { data } = await api.get(`/foncier-clair/${encodeURIComponent(tk)}`);
      setDossier(data);
    } catch (e) {
      setError(formatApiError(e) || "Dossier introuvable.");
    }
  }, []);
  useEffect(() => { if (token) loadDossier(token); }, [token, loadDossier]);

  // ---- Validation par étape
  const canNext = useMemo(() => {
    if (step === 0) return situation.territoire && situation.type_bien && situation.succession_indivision;
    if (step === 1) {
      const y = parseInt(situation.annee_debut, 10);
      const yOk = y && y >= 1900 && y <= CURRENT_YEAR;
      return yOk && possession.continue && possession.paisible && possession.publique && possession.comportement_proprio && possession.conteste;
    }
    if (step === 2) return titre.juste_titre && titre.bonne_foi;
    return true;
  }, [step, situation, possession, titre]);

  const goNext = () => { if (canNext) setStep(step + 1); };
  const goPrev = () => { if (step > 0) setStep(step - 1); };

  const teaserPreview = useMemo(() => {
    // Verdict local (approximatif, uniquement pour affichage teaser)
    const nonExplicites = ["continue", "paisible", "publique", "comportement_proprio"].filter((k) => possession[k] === "non");
    if (possession.conteste === "oui" || nonExplicites.length) return { level: "danger", title: "Conditions non réunies en l'état", teaser: "Certaines des cinq conditions cumulatives du Code civil ne sont pas remplies." };
    if (!annees) return { level: "warn", title: "Année à renseigner", teaser: "Renseignez l'année de début d'occupation pour affiner." };
    const jtbf = titre.juste_titre === "oui" && titre.bonne_foi === "oui";
    if (territoireOM && annees >= 10 && CURRENT_YEAR <= 2038) return { level: "success", title: "Éligibilité vraisemblable", teaser: "Un régime spécifique outre-mer semble applicable — le détail complet est disponible dans votre fiche." };
    if (jtbf && annees >= 10) return { level: "success", title: "Éligibilité vraisemblable", teaser: "Le régime abrégé de droit commun semble applicable — le détail complet est disponible dans votre fiche." };
    if (annees >= 30) return { level: "success", title: "Éligibilité vraisemblable", teaser: "Le régime de droit commun semble applicable — le détail complet est disponible dans votre fiche." };
    return { level: "warn", title: "Pas encore éligible", teaser: "Il vous manque un certain nombre d'années — le décompte précis figure dans votre fiche." };
  }, [possession, titre, annees, territoireOM]);

  // ---- Submit (crée dossier serveur + retourne token)
  const submitDossier = async ({ withPromo = false } = {}) => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        prenom: contact.prenom.trim(),
        nom: contact.nom.trim(),
        email: contact.email.trim().toLowerCase(),
        telephone: contact.telephone.trim(),
        situation: { ...situation, annee_debut: parseInt(situation.annee_debut, 10) || null },
        possession,
        titre,
        documents,
        wants_callback: wantsCallback === "oui",
        consent_transmission: consentTransmission,
        waive_retractation: waiveRetractation,
      };
      const { data } = await api.post("/foncier-clair/submit", payload);
      setToken(data.public_token);
      setParams({ dossier: data.public_token });
      if (withPromo) {
        // Applique le code promo directement
        try {
          await api.post(`/foncier-clair/${data.public_token}/apply-promo`, { code: promoCode.trim() });
        } catch (e) {
          setError(formatApiError(e) || "Code promo invalide.");
          await loadDossier(data.public_token);
          setSubmitting(false);
          return;
        }
        await loadDossier(data.public_token);
      } else {
        // Ouverture du paiement Stripe
        const pl = await api.get(`/foncier-clair/${data.public_token}/payment-link`);
        window.open(pl.data.url, "_blank", "noopener,noreferrer");
        pollForPayment(data.public_token);
      }
    } catch (e) {
      setError(formatApiError(e) || "Erreur d'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  const pollForPayment = useCallback(async (tk) => {
    setPollingPayment(true);
    for (let i = 0; i < 20; i++) {
      try {
        const { data } = await api.get(`/foncier-clair/${encodeURIComponent(tk)}`);
        if (data.paid) { setDossier(data); setPollingPayment(false); return; }
      } catch (_e) {}
      await new Promise((r) => setTimeout(r, 3000));
    }
    setPollingPayment(false);
  }, []);

  const applyPromoOnDossier = async () => {
    if (!token) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/foncier-clair/${token}/apply-promo`, { code: promoCode.trim() });
      await loadDossier(token);
    } catch (e) {
      setError(formatApiError(e) || "Code promo invalide.");
    } finally {
      setSubmitting(false);
    }
  };

  const openPayment = async () => {
    try {
      const { data } = await api.get(`/foncier-clair/${token}/payment-link`);
      window.open(data.url, "_blank", "noopener,noreferrer");
      pollForPayment(token);
    } catch (e) { setError(formatApiError(e)); }
  };

  const resendPDF = async () => {
    try {
      await api.post(`/foncier-clair/${token}/resend-pdf`);
      alert(`Fiche renvoyée à ${dossier.email}`);
    } catch (e) { alert(formatApiError(e)); }
  };
  const downloadPDF = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/foncier-clair/${token}/pdf`, "_blank");
  };

  // ---- Checkout validity
  const emailOk = EMAIL_RE.test(contact.email.trim());
  const phoneRequired = wantsCallback === "oui";
  const phoneOk = !phoneRequired || contact.telephone.trim().length >= 4;
  const promoValidLocal = (promoCode.trim().length >= 4);
  const canCheckout = contact.prenom.trim() && contact.nom.trim() && emailOk && (wantsCallback === "oui" || wantsCallback === "non") && phoneOk && waiveRetractation;
  const canPromo = canCheckout && promoValidLocal;

  const checkoutHelpMsg = (() => {
    if (!contact.prenom.trim()) return "Renseignez votre prénom.";
    if (!contact.nom.trim()) return "Renseignez votre nom.";
    if (!emailOk) return "Renseignez un email valide.";
    if (wantsCallback !== "oui" && wantsCallback !== "non") return "Indiquez si vous souhaitez être rappelé·e.";
    if (phoneRequired && !phoneOk) return "Un numéro de téléphone est requis pour un rappel.";
    if (!waiveRetractation) return "Vous devez renoncer au droit de rétractation pour la livraison immédiate du contenu.";
    return "";
  })();

  return (
    <div style={{ background: FC.bg, minHeight: "100vh", fontFamily: FONT_SANS, color: FC.ink }} data-testid="foncier-clair-app">
      <FCHeader />
      {(!token || step === 4 || dossier) ? null : <FCProgress step={step} />}
      <main style={{ maxWidth: 880, margin: "0 auto", padding: "24px 20px 60px" }}>
        {error && <div data-testid="fc-error" style={{ background: "rgba(156,59,59,0.08)", border: `1px solid ${FC.red}40`, color: FC.red, padding: "10px 14px", borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        {/* Dossier chargé (post-paiement/promo) */}
        {dossier ? (
          <ResultView dossier={dossier} downloadPDF={downloadPDF} resendPDF={resendPDF}
                     openPayment={openPayment} pollingPayment={pollingPayment}
                     promoCode={promoCode} setPromoCode={setPromoCode}
                     applyPromo={applyPromoOnDossier} submitting={submitting} error={error} />
        ) : checkoutMode ? (
          <CheckoutView
            contact={contact} setContact={setContact}
            wantsCallback={wantsCallback} setWantsCallback={setWantsCallback}
            consentTransmission={consentTransmission} setConsentTransmission={setConsentTransmission}
            waiveRetractation={waiveRetractation} setWaiveRetractation={setWaiveRetractation}
            promoCode={promoCode} setPromoCode={setPromoCode}
            canCheckout={canCheckout} canPromo={canPromo}
            helpMsg={checkoutHelpMsg}
            onPay={() => submitDossier({ withPromo: false })}
            onApplyPromo={() => submitDossier({ withPromo: true })}
            onBack={() => setCheckoutMode(false)}
            submitting={submitting}
            teaserPreview={teaserPreview} annees={annees}
          />
        ) : (
          <>
            {/* Wizard */}
            {step === 0 && <Step1 situation={situation} setSituation={setSituation} />}
            {step === 1 && <Step2 situation={situation} setSituation={setSituation} possession={possession} setPossession={setPossession} />}
            {step === 2 && <Step3 titre={titre} setTitre={setTitre} documents={documents} setDocuments={setDocuments} />}
            {step === 3 && <Step4Result teaser={teaserPreview} annees={annees} territoireOM={territoireOM} onUnlock={() => setCheckoutMode(true)} />}

            {step < 3 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 26, gap: 12 }}>
                {step > 0 ? (
                  <FCBtnOutline onClick={goPrev} testid="fc-btn-prev">← Précédent</FCBtnOutline>
                ) : <span />}
                <FCBtnPrimary onClick={goNext} disabled={!canNext} testid={`fc-btn-next-${step}`}>Suivant →</FCBtnPrimary>
              </div>
            )}
          </>
        )}
      </main>
      <FCFooter />
    </div>
  );
}

/* ================================================================
 * COMPOSANTS D'UI
 * ================================================================ */
function FCHeader() {
  return (
    <header style={{ background: FC.navy, padding: "20px 24px", borderBottom: `3px solid ${FC.gold}` }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ background: FC.gold, color: FC.navyDark, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 700 }}>FC</div>
          <div>
            <div style={{ color: "#FFF", fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 700, letterSpacing: "0.02em" }}>Foncier Clair</div>
            <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: FONT_MONO }}>Prescription acquisitive · Outre-mer</div>
          </div>
        </div>
        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11.5, fontStyle: "italic", letterSpacing: "0.04em" }}>
          Un outil <b style={{ color: FC.gold, fontStyle: "normal" }}>AUPRÉA</b>
        </div>
      </div>
    </header>
  );
}
function FCFooter() {
  return (
    <footer style={{ background: FC.surfaceAlt, padding: "20px 24px", borderTop: `1px solid ${FC.line}`, marginTop: 40 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", fontSize: 11.5, color: FC.muted, textAlign: "center", lineHeight: 1.6 }}>
        AUPRÉA SAS · SIRET 933 283 459 00016 · Grand-Bourg, Guadeloupe · <a href="mailto:contact@auprea.com" style={{ color: FC.navy }}>contact@auprea.com</a> · www.auprea.com
      </div>
    </footer>
  );
}
function FCProgress({ step }) {
  return (
    <div style={{ background: FC.surface, padding: "14px 20px", borderBottom: `1px solid ${FC.line}` }}>
      <div style={{ maxWidth: 880, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }} data-testid="fc-progress">
        {STEPS.map((s, i) => (
          <div key={s.id} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 130 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: i <= step ? FC.navy : FC.surfaceAlt, color: i <= step ? "#fff" : FC.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: FONT_MONO }}>{i + 1}</div>
            <div style={{ fontSize: 12.5, color: i <= step ? FC.ink : FC.muted, fontWeight: i === step ? 600 : 400 }}>{s.label}</div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? FC.navy : FC.line, marginLeft: 4 }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Champs réutilisables ---- */
function Card({ children, style }) {
  return <div style={{ background: FC.surface, border: `1px solid ${FC.line}`, padding: "22px 24px", ...style }}>{children}</div>;
}
function StepTitle({ n, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ color: FC.gold, fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Étape {n} / 4</div>
      <h1 style={{ fontFamily: FONT_SERIF, fontSize: 30, fontWeight: 600, margin: 0, color: FC.navy }}>{title}</h1>
      {subtitle && <div style={{ color: FC.muted, marginTop: 6, fontSize: 14, lineHeight: 1.5 }}>{subtitle}</div>}
    </div>
  );
}
function Label({ children, hint }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: FC.ink }}>{children}</div>
      {hint && <div style={{ fontSize: 12, color: FC.muted, marginTop: 2, fontFamily: FONT_MONO }}>{hint}</div>}
    </div>
  );
}
function RadioGroup({ value, onChange, options, testid }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid={testid}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            data-testid={`${testid}-${o.v.replace(/\s+/g, '-')}`}
            style={{
              padding: "10px 18px", fontSize: 14, fontFamily: FONT_SANS, fontWeight: 500,
              background: active ? FC.navy : FC.surface, color: active ? "#FFF" : FC.ink,
              border: `1.5px solid ${active ? FC.navy : FC.line}`, borderRadius: 6,
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}
function TextInput({ value, onChange, type = "text", placeholder, testid, min, max }) {
  return (
    <input
      type={type} value={value} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} min={min} max={max}
      data-testid={testid}
      style={{ width: "100%", padding: "10px 14px", fontSize: 14, fontFamily: FONT_SANS, border: `1.5px solid ${FC.line}`, borderRadius: 6, background: FC.surface, color: FC.ink }}
    />
  );
}
function FCBtnPrimary({ children, onClick, disabled, testid, gold = false }) {
  const bg = gold ? FC.gold : FC.navy;
  const color = gold ? FC.navyDark : "#FFF";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      style={{
        padding: "12px 26px", fontSize: 15, fontWeight: 700, fontFamily: FONT_SANS,
        background: bg, color, border: "none", borderRadius: 6,
        opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer",
        letterSpacing: "0.01em",
      }}>
      {children}
    </button>
  );
}
function FCBtnOutline({ children, onClick, testid, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      style={{
        padding: "10px 20px", fontSize: 14, fontWeight: 500, fontFamily: FONT_SANS,
        background: "transparent", color: FC.navy, border: `1.5px solid ${FC.navy}`, borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
      }}>
      {children}
    </button>
  );
}

/* ---- STEP 1 ---- */
function Step1({ situation, setSituation }) {
  const set = (k, v) => setSituation((s) => ({ ...s, [k]: v }));
  return (
    <div data-testid="fc-step-1">
      <StepTitle n={1} title="Le bien concerné" subtitle="Quelques informations essentielles pour situer votre bien." />
      <Card>
        <div style={{ marginBottom: 18 }}>
          <Label>Territoire</Label>
          <select value={situation.territoire} onChange={(e) => set("territoire", e.target.value)} data-testid="fc-territoire"
            style={{ width: "100%", padding: "10px 14px", fontSize: 14, fontFamily: FONT_SANS, border: `1.5px solid ${FC.line}`, borderRadius: 6, background: FC.surface, color: FC.ink }}>
            <option value="">Sélectionnez…</option>
            {TERRITOIRES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 18 }}>
          <Label>Type de bien</Label>
          <RadioGroup value={situation.type_bien} onChange={(v) => set("type_bien", v)} options={TYPES_BIEN} testid="fc-type-bien" />
        </div>
        <div>
          <Label hint="Successions bloquées, indivisions non partagées — très fréquent outre-mer.">Ce bien fait-il partie d'une succession non réglée ou d'une indivision ?</Label>
          <RadioGroup value={situation.succession_indivision} onChange={(v) => set("succession_indivision", v)} options={YN_NSP} testid="fc-succ-indiv" />
        </div>
      </Card>
    </div>
  );
}
/* ---- STEP 2 ---- */
function Step2({ situation, setSituation, possession, setPossession }) {
  const setSit = (k, v) => setSituation((s) => ({ ...s, [k]: v }));
  const setPos = (k, v) => setPossession((p) => ({ ...p, [k]: v }));
  const questions = [
    { k: "continue", l: "Votre occupation est-elle ininterrompue depuis cette date ?", article: "Art. 2258 C. civ." },
    { k: "paisible", l: "Cette possession est-elle paisible (sans violence, ni contrainte) ?", article: "Art. 2261 C. civ." },
    { k: "publique", l: "Cette possession est-elle publique et visible (non clandestine) ?", article: "Art. 2261 C. civ." },
    { k: "comportement_proprio", l: "Vous comportez-vous en propriétaire (entretien, travaux, taxe foncière) ?", article: "Art. 2255 C. civ." },
    { k: "conteste", l: "Votre possession est-elle contestée par un tiers (héritier, voisin, ancien propriétaire) ?", article: "Si Oui → possession équivoque" },
  ];
  return (
    <div data-testid="fc-step-2">
      <StepTitle n={2} title="Votre possession du bien" subtitle="Les cinq conditions cumulatives du Code civil." />
      <Card style={{ marginBottom: 18 }}>
        <Label hint={`Année entière entre 1900 et ${CURRENT_YEAR}.`}>Année de début d'occupation continue</Label>
        <TextInput value={situation.annee_debut} onChange={(v) => setSit("annee_debut", v.replace(/[^0-9]/g, "").slice(0, 4))} type="number" min={1900} max={CURRENT_YEAR} placeholder="Ex. 1995" testid="fc-annee-debut" />
      </Card>
      {questions.map((q) => (
        <Card key={q.k} style={{ marginBottom: 12 }}>
          <Label hint={q.article}>{q.l}</Label>
          <RadioGroup value={possession[q.k]} onChange={(v) => setPos(q.k, v)} options={YN_NSP} testid={`fc-pos-${q.k}`} />
        </Card>
      ))}
    </div>
  );
}
/* ---- STEP 3 ---- */
function Step3({ titre, setTitre, documents, setDocuments }) {
  const setT = (k, v) => setTitre((t) => ({ ...t, [k]: v }));
  const toggleDoc = (k) => {
    setDocuments((docs) => {
      if (k === "aucun") return docs.includes("aucun") ? [] : ["aucun"];
      const withoutAucun = docs.filter((d) => d !== "aucun");
      return withoutAucun.includes(k) ? withoutAucun.filter((d) => d !== k) : [...withoutAucun, k];
    });
  };
  return (
    <div data-testid="fc-step-3">
      <StepTitle n={3} title="Titre et preuves disponibles" subtitle="Ces éléments peuvent activer un régime accéléré ou compléter votre dossier." />
      <Card style={{ marginBottom: 12 }}>
        <Label hint="Un acte translatif de propriété — même imparfait (art. 2272 al. 2 C. civ.).">Disposez-vous d'un « juste titre » ?</Label>
        <RadioGroup value={titre.juste_titre} onChange={(v) => setT("juste_titre", v)} options={YN_NSP} testid="fc-juste-titre" />
      </Card>
      <Card style={{ marginBottom: 18 }}>
        <Label hint="Vous étiez convaincu·e, au moment de l'obtenir, que cet acte transférait bien la propriété.">Étiez-vous de bonne foi au moment de l'acquisition ?</Label>
        <RadioGroup value={titre.bonne_foi} onChange={(v) => setT("bonne_foi", v)} options={YN_NSP} testid="fc-bonne-foi" />
      </Card>
      <Card>
        <Label hint="Sélectionnez tous les documents que vous pouvez fournir.">Preuves disponibles</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {DOCS_LIST.map((d) => {
            const on = documents.includes(d.k);
            return (
              <label key={d.k} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 10px", borderRadius: 4, background: on ? FC.surfaceAlt : "transparent" }}>
                <input type="checkbox" checked={on} onChange={() => toggleDoc(d.k)} data-testid={`fc-doc-${d.k}`} style={{ marginTop: 3, accentColor: FC.navy }} />
                <span style={{ fontSize: 13.5, color: FC.ink, lineHeight: 1.4 }}>{d.l}</span>
              </label>
            );
          })}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 10px", borderRadius: 4, marginTop: 6, borderTop: `1px dashed ${FC.line}` }}>
            <input type="checkbox" checked={documents.includes("aucun")} onChange={() => toggleDoc("aucun")} data-testid="fc-doc-aucun" style={{ marginTop: 3, accentColor: FC.muted }} />
            <span style={{ fontSize: 13.5, color: FC.muted, fontStyle: "italic" }}>Aucun de ces documents pour l'instant</span>
          </label>
        </div>
      </Card>
    </div>
  );
}

/* ---- STEP 4 (résultat teaser) ---- */
function Step4Result({ teaser, annees, territoireOM, onUnlock }) {
  const colorMap = { success: FC.teal, warn: FC.gold, danger: FC.red };
  const c = colorMap[teaser.level] || FC.navy;
  return (
    <div data-testid="fc-step-4">
      <StepTitle n={4} title="Votre diagnostic préliminaire" subtitle="Ce que nous pouvons dire à ce stade — le détail complet est disponible dans votre fiche." />
      <Card style={{ borderLeft: `4px solid ${c}`, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ background: c, color: "#FFF", padding: "4px 12px", fontSize: 11, fontWeight: 700, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", borderRadius: 3 }} data-testid="fc-teaser-level">
            {teaser.level === "success" ? "Éligibilité vraisemblable" : teaser.level === "warn" ? "Attention" : "Conditions non réunies"}
          </div>
          {annees ? <div style={{ fontFamily: FONT_MONO, color: FC.muted, fontSize: 12 }}>{annees} an{annees > 1 ? "s" : ""} d'occupation</div> : null}
        </div>
        <h3 style={{ margin: 0, fontFamily: FONT_SERIF, fontSize: 22, color: FC.navy }}>{teaser.title}</h3>
        <p style={{ marginTop: 8, color: FC.ink, lineHeight: 1.6, fontSize: 14.5 }}>{teaser.teaser}</p>
      </Card>
      {/* Teaser flouté */}
      <Card style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ filter: "blur(4.5px)", pointerEvents: "none", userSelect: "none" }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: FC.gold, letterSpacing: "0.15em", textTransform: "uppercase" }}>Régime identifié</div>
          <h3 style={{ margin: "6px 0 12px", fontFamily: FONT_SERIF, fontSize: 20, color: FC.navy }}>Régime accéléré Outre-mer — 10 ans</h3>
          <p style={{ color: FC.muted, fontSize: 14 }}>Loi n° 2024-322 du 9 avril 2024, art. 1 — Code civil, art. 2258 et suivants…</p>
          <ul style={{ paddingLeft: 20, color: FC.muted, fontSize: 13.5 }}>
            <li>Vous justifiez de X années sur un territoire ultramarin éligible…</li>
            <li>Ce régime spécifique ramène le délai de 30 à 10 ans…</li>
            <li>Recommandation destinée à un notaire compétent…</li>
          </ul>
        </div>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.55)" }}>
          <div style={{ fontSize: 40, marginBottom: 6 }}>🔒</div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: FC.muted, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>Contenu verrouillé</div>
          <FCBtnPrimary onClick={onUnlock} gold testid="fc-btn-unlock">Débloquer mon résultat · 69 €</FCBtnPrimary>
          <div style={{ marginTop: 10, fontSize: 12, color: FC.muted }}>Résultat immédiat par e-mail · Paiement sécurisé Stripe</div>
        </div>
      </Card>
    </div>
  );
}

/* ---- CHECKOUT ---- */
function CheckoutView({ contact, setContact, wantsCallback, setWantsCallback, consentTransmission, setConsentTransmission, waiveRetractation, setWaiveRetractation, promoCode, setPromoCode, canCheckout, canPromo, helpMsg, onPay, onApplyPromo, onBack, submitting, teaserPreview, annees }) {
  const set = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  return (
    <div data-testid="fc-checkout">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <FCBtnOutline onClick={onBack} testid="fc-btn-back-to-result">← Retour au diagnostic</FCBtnOutline>
      </div>
      <StepTitle n="—" title="Finaliser mon accès à la fiche" subtitle="Vos coordonnées permettent de vous envoyer immédiatement votre fiche par e-mail après paiement." />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <Badge>🔒 Paiement sécurisé</Badge>
        <Badge>⚡ Résultat immédiat par email</Badge>
        <Badge>🔁 Rappel partenaire sans engagement</Badge>
      </div>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><Label>Prénom</Label><TextInput value={contact.prenom} onChange={(v) => set("prenom", v)} testid="fc-input-prenom" /></div>
          <div><Label>Nom</Label><TextInput value={contact.nom} onChange={(v) => set("nom", v)} testid="fc-input-nom" /></div>
          <div style={{ gridColumn: "1 / span 2" }}><Label>Email</Label><TextInput type="email" value={contact.email} onChange={(v) => set("email", v)} testid="fc-input-email" /></div>
          <div style={{ gridColumn: "1 / span 2" }}>
            <Label>Souhaitez-vous être rappelé·e par l'un de nos partenaires ?</Label>
            <RadioGroup value={wantsCallback} onChange={setWantsCallback} options={YN} testid="fc-wants-callback" />
          </div>
          {wantsCallback === "oui" && (
            <div style={{ gridColumn: "1 / span 2" }}>
              <Label>Téléphone (requis pour un rappel)</Label>
              <TextInput type="tel" value={contact.telephone} onChange={(v) => set("telephone", v)} testid="fc-input-tel" />
            </div>
          )}
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={waiveRetractation} onChange={(e) => setWaiveRetractation(e.target.checked)} data-testid="fc-waive-retractation" style={{ marginTop: 3, accentColor: FC.navy }} />
          <span style={{ fontSize: 13.5, color: FC.ink, lineHeight: 1.5 }}>
            Je renonce expressément à mon <b>droit de rétractation de 14 jours</b> pour bénéficier de la livraison immédiate de ma fiche <span style={{ fontFamily: FONT_MONO, fontSize: 12 }}>(art. L221-28 13° du Code de la consommation)</span>. <b style={{ color: FC.red }}>Obligatoire.</b>
          </span>
        </label>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={consentTransmission} onChange={(e) => setConsentTransmission(e.target.checked)} data-testid="fc-consent-transmission" style={{ marginTop: 3, accentColor: FC.navy }} />
          <span style={{ fontSize: 13.5, color: FC.ink, lineHeight: 1.5 }}>
            J'autorise la transmission de mon dossier à un partenaire professionnel d'AUPRÉA pour être accompagné·e (facultatif — ne bloque pas l'achat).
          </span>
        </label>
      </Card>

      {/* Code promo interne */}
      <Card style={{ marginBottom: 18, background: FC.surfaceAlt, borderStyle: "dashed" }}>
        <Label hint="Réservé aux démonstrations commerciales AUPRÉA.">Code promo (facultatif)</Label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextInput value={promoCode} onChange={setPromoCode} placeholder="Saisir votre code" testid="fc-promo-code" />
          <FCBtnOutline onClick={onApplyPromo} disabled={!canPromo || submitting} testid="fc-btn-apply-promo">{submitting ? "…" : "Valider"}</FCBtnOutline>
        </div>
      </Card>

      {helpMsg && <div style={{ background: "rgba(201,162,39,0.10)", border: `1px solid ${FC.gold}55`, color: FC.navy, padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13 }} data-testid="fc-help-msg">{helpMsg}</div>}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ color: FC.muted, fontSize: 13 }}>Aperçu : {teaserPreview.title}{annees ? ` · ${annees} an${annees > 1 ? "s" : ""}` : ""}</div>
        <FCBtnPrimary onClick={onPay} disabled={!canCheckout || submitting} gold testid="fc-btn-pay">
          {submitting ? "Envoi…" : "Payer 69 € et recevoir ma fiche →"}
        </FCBtnPrimary>
      </div>
    </div>
  );
}
function Badge({ children }) {
  return <div style={{ background: FC.surface, border: `1px solid ${FC.line}`, padding: "6px 12px", fontSize: 12, color: FC.muted, borderRadius: 999, fontFamily: FONT_MONO }}>{children}</div>;
}

/* ---- RESULT (paid) ---- */
function ResultView({ dossier, downloadPDF, resendPDF, openPayment, pollingPayment, promoCode, setPromoCode, applyPromo, submitting, error }) {
  const paid = !!dossier.paid;
  const isDemo = !!dossier.is_demo;
  const r = dossier.result || {};
  if (!paid) {
    return (
      <div data-testid="fc-result-locked">
        <StepTitle n="—" title="Votre dossier est enregistré" subtitle={`Référence ${dossier.reference}. Débloquez votre fiche pour recevoir le détail par e-mail.`} />
        <Card style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: FC.gold, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>Aperçu</div>
          <div style={{ fontSize: 14, color: FC.ink, marginBottom: 12 }}>
            Niveau : <b style={{ color: r.level === "success" ? FC.teal : r.level === "danger" ? FC.red : FC.gold }}>{r.level}</b>
            {r.annees != null && ` · ${r.annees} an${r.annees > 1 ? "s" : ""} calculés`}
          </div>
        </Card>
        <Card style={{ background: FC.surfaceAlt, borderStyle: "dashed", marginBottom: 12 }}>
          <Label hint="Réservé aux démonstrations commerciales AUPRÉA.">Code promo</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <TextInput value={promoCode} onChange={setPromoCode} placeholder="Saisir votre code" testid="fc-result-promo-code" />
            <FCBtnOutline onClick={applyPromo} disabled={submitting || !promoCode.trim()} testid="fc-result-btn-apply-promo">Valider</FCBtnOutline>
          </div>
          {error && <div style={{ color: FC.red, marginTop: 8, fontSize: 13 }}>{error}</div>}
        </Card>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          {pollingPayment && <span style={{ fontSize: 13, color: FC.teal, alignSelf: "center" }} data-testid="fc-polling">En attente de confirmation Stripe…</span>}
          <FCBtnPrimary onClick={openPayment} gold testid="fc-btn-pay-again">Payer 69 € →</FCBtnPrimary>
        </div>
      </div>
    );
  }
  const cMap = { success: FC.teal, warn: FC.gold, danger: FC.red };
  const c = cMap[r.level] || FC.navy;
  return (
    <div data-testid="fc-result-paid">
      {isDemo && (
        <div style={{ background: FC.red, color: "#FFF", padding: "10px 16px", borderRadius: 4, textAlign: "center", fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 16 }} data-testid="fc-demo-banner">
          Dossier de démonstration — Aucun paiement perçu
        </div>
      )}
      <StepTitle n="—" title={`Fiche ${dossier.reference}`} subtitle={`Diagnostic établi pour ${dossier.prenom} ${dossier.nom} · ${dossier.email}`} />
      <Card style={{ borderLeft: `4px solid ${c}`, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ background: c, color: "#FFF", padding: "4px 12px", fontSize: 11, fontWeight: 700, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", borderRadius: 3 }}>
            {r.level === "success" ? "Éligibilité vraisemblable" : r.level === "warn" ? "Attention" : "Conditions non réunies"}
          </div>
          <div style={{ fontFamily: FONT_MONO, color: FC.muted, fontSize: 12 }}>{r.annees} an{r.annees > 1 ? "s" : ""}</div>
        </div>
        <h3 style={{ margin: 0, fontFamily: FONT_SERIF, fontSize: 22, color: FC.navy }}>{r.regime_title}</h3>
        <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: FC.gold, marginTop: 6, letterSpacing: "0.04em" }}>{r.base_legale}</div>
        <div style={{ marginTop: 12, color: FC.ink, lineHeight: 1.65, fontSize: 14 }}>
          {(r.regime_detail || []).map((line, i) => <p key={i} style={{ margin: "6px 0" }}>{line}</p>)}
        </div>
      </Card>

      {r.notoriete_reco && (
        <Card style={{ background: FC.surfaceAlt, marginBottom: 16 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: FC.teal, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>Facilitation légale</div>
          <p style={{ margin: 0, color: FC.ink, fontSize: 14, lineHeight: 1.6 }}>Un <b>acte de notoriété acquisitive</b> peut être dressé par notaire, avec publication au fichier immobilier avant le <b>31 décembre 2027</b>, contestable uniquement dans les 5 ans suivant sa publication.</p>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 10px 0", fontFamily: FONT_SERIF, fontSize: 16, color: FC.navy }}>Recommandations</h4>
        <ul style={{ paddingLeft: 20, color: FC.ink, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
          {(r.conseils || []).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </Card>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <FCBtnPrimary onClick={downloadPDF} testid="fc-btn-download">Télécharger le PDF</FCBtnPrimary>
        <FCBtnOutline onClick={resendPDF} testid="fc-btn-resend">Renvoyer par e-mail</FCBtnOutline>
      </div>
    </div>
  );
}
